import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import so from './so.json';

/**
 * Raad i18n. English and Somali are BOTH first-class — every user-facing string
 * resolves through `t(...)`, never a hardcoded literal, from the first commit.
 * Somali is Latin-script and LTR, so there is no `dir` flip; only
 * `<html lang>` changes between 'en' and 'so'.
 *
 * SOMALI COPY IS PROVISIONAL, PENDING NATIVE REVIEW (as of 2026-08-10).
 * The strings in `so.json` were authored by the build, not yet validated by a
 * native speaker — Mahad has that review scheduled before slice 07. Do NOT read
 * "it shipped" as "it was checked": treat every SO leaf as a draft that may be
 * reworded, and do not add code, comments, or tests that assert the Somali is
 * final. (`so.json` is strict JSON and can carry no comment of its own, and a
 * metadata key would break the en/so key-parity test — so the note lives here,
 * at the single point where the file is loaded.)
 */

export const SUPPORTED_LANGS = ['en', 'so'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

export const LANG_STORAGE_KEY = 'raad.lang';
const DEFAULT_LANG: Lang = 'en';

function isLang(value: unknown): value is Lang {
  return value === 'en' || value === 'so';
}

/**
 * Pre-auth language resolution (source-of-truth order):
 *   1. explicit in-app selection persisted to localStorage
 *   2. navigator.language (so / en)
 *   3. default 'en'
 * After login the signed-in user's `language` from /api/me is authoritative and
 * is written back to localStorage — see useAuth.
 */
export function resolveInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* localStorage can throw in private mode — fall through to navigator */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2) : '';
  if (isLang(nav)) return nav;
  return DEFAULT_LANG;
}

export function persistLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    /* non-fatal: a language preference that cannot be persisted is not an error */
  }
}

/**
 * Applies the language to i18next AND to the document element. `dir` stays
 * 'ltr' — Somali does not flip it. Persistence is the caller's choice, so a
 * transient render (a test asserting the SO copy) does not clobber the user's
 * saved preference.
 */
export function setLanguage(lang: Lang, opts: { persist?: boolean } = {}): void {
  void i18n.changeLanguage(lang);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
    document.documentElement.dir = 'ltr';
  }
  if (opts.persist) persistLang(lang);
}

const isTest = import.meta.env.MODE === 'test';

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    so: { translation: so },
  },
  lng: resolveInitialLang(),
  fallbackLng: false, // no silent EN fallback — a missing SO key must surface
  supportedLngs: SUPPORTED_LANGS,
  interpolation: { escapeValue: false },
  returnNull: false,
  // i18next only invokes `missingKeyHandler` when `saveMissing` is true (see
  // i18next.js: the handler call sits inside `if (this.options.saveMissing)`).
  // So the guard is armed ONLY in test — where an unknown key becomes a hard
  // failure — and stays inert (no-op) in dev/prod. Not armed outside test on
  // purpose: we never want a runtime throw on a user's screen for a stray key.
  saveMissing: isTest,
  missingKeyHandler: isTest
    ? (_lngs, _ns, key) => {
        throw new Error(`i18n: missing translation key "${key}"`);
      }
    : undefined,
});

// Keep <html lang> in sync with the boot language.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
  document.documentElement.dir = 'ltr';
}

export default i18n;
